import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { getCurrentCompanyId } from '../lib/company';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Plus, Trash2, ShoppingBag, Landmark, Receipt, Users2, TrendingUp, Lock, Unlock, DollarSign, Download } from 'lucide-react';
import { downloadBlob } from '../lib/salespro';
import { toast } from 'sonner';

const money = (n) => `$${Number(n || 0).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const currentPeriod = () => new Date().toISOString().slice(0, 7);

async function downloadReport(promise, fallbackName) {
  try {
    const res = await promise;
    const cd = res.headers?.['content-disposition'] || '';
    const m = cd.match(/filename=([^;]+)/);
    downloadBlob(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), m ? m[1].trim() : fallbackName);
    toast.success('SalesPRO: Reporte descargado');
  } catch (err) {
    toast.error(err.response?.data?.detail || 'SalesPRO: Error al generar reporte');
  }
}

export default function FinancePro() {
  const { user } = useAuth();
  const allowed = ['master', 'superadmin', 'admin'].includes(user?.role_id);
  const companyId = getCurrentCompanyId();
  const [tab, setTab] = useState('resumen');
  const [banks, setBanks] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [expCats, setExpCats] = useState([]);
  const [products, setProducts] = useState([]);
  const [closures, setClosures] = useState([]);
  const [period, setPeriod] = useState(currentPeriod());
  const [pnl, setPnl] = useState(null);

  const loadAll = useCallback(async () => {
    if (!allowed) return;
    try {
      const params = { company_id: companyId };
      const [b, s, p, e, ec, prod, cl] = await Promise.all([
        api.getBanks(params), api.getSuppliers(params), api.getPurchases(params),
        api.getExpenses(params), api.getExpenseCategories(params),
        api.getProducts(params), api.getClosures(params)
      ]);
      setBanks(b.data); setSuppliers(s.data); setPurchases(p.data);
      setExpenses(e.data); setExpCats(ec.data); setProducts(prod.data); setClosures(cl.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'SalesPRO: Error al cargar finanzas');
    }
  }, [companyId, allowed]);

  const loadPnl = useCallback(async () => {
    if (!allowed) return;
    try {
      const res = await api.getProfitLoss(companyId, period);
      setPnl(res.data);
    } catch (err) {
      setPnl(null);
    }
  }, [companyId, period, allowed]);

  useEffect(() => { (async () => { await loadAll(); })(); }, [loadAll]);
  useEffect(() => { (async () => { await loadPnl(); })(); }, [loadPnl]);

  if (!allowed) return <Navigate to="/dashboard" replace />;

  const bankName = (id) => banks.find(b => b.account_id === id)?.name || '—';
  const supplierName = (id) => suppliers.find(s => s.supplier_id === id)?.name || '—';

  return (
    <div className="space-y-6" data-testid="finance-pro-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#022C22]">Compras & Finanzas</h1>
        <p className="text-sm text-[#64748B] mt-1">Compras, bancos, gastos y auditoría financiera por empresa</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto" data-testid="finance-tabs">
          <TabsTrigger value="resumen" data-testid="tab-resumen"><TrendingUp size={15} className="mr-1.5" />Resumen</TabsTrigger>
          <TabsTrigger value="compras" data-testid="tab-compras"><ShoppingBag size={15} className="mr-1.5" />Compras</TabsTrigger>
          <TabsTrigger value="gastos" data-testid="tab-gastos"><Receipt size={15} className="mr-1.5" />Gastos</TabsTrigger>
          <TabsTrigger value="bancos" data-testid="tab-bancos"><Landmark size={15} className="mr-1.5" />Bancos</TabsTrigger>
          <TabsTrigger value="proveedores" data-testid="tab-proveedores"><Users2 size={15} className="mr-1.5" />Proveedores</TabsTrigger>
        </TabsList>

        <TabsContent value="resumen">
          <ResumenTab period={period} setPeriod={setPeriod} pnl={pnl} closures={closures} reload={() => { loadAll(); loadPnl(); }} companyId={companyId} />
        </TabsContent>
        <TabsContent value="compras">
          <ComprasTab purchases={purchases} suppliers={suppliers} products={products} banks={banks} companyId={companyId} reload={() => { loadAll(); loadPnl(); }} bankName={bankName} supplierName={supplierName} />
        </TabsContent>
        <TabsContent value="gastos">
          <GastosTab expenses={expenses} expCats={expCats} banks={banks} companyId={companyId} reload={() => { loadAll(); loadPnl(); }} bankName={bankName} />
        </TabsContent>
        <TabsContent value="bancos">
          <BancosTab banks={banks} companyId={companyId} reload={loadAll} />
        </TabsContent>
        <TabsContent value="proveedores">
          <ProveedoresTab suppliers={suppliers} companyId={companyId} reload={loadAll} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------- Resumen / P&L / Cierres ---------------- */
function ResumenTab({ period, setPeriod, pnl, closures, reload, companyId }) {
  const t = pnl?.totals || {};
  const closure = pnl?.closure;

  const doClose = async (status) => {
    if (status === 'definitivo' && !window.confirm('SalesPRO: Un cierre definitivo bloquea la edición del período. ¿Continuar?')) return;
    try {
      await api.createClosure({ company_id: companyId, period, status });
      toast.success(`SalesPRO: Cierre ${status} registrado`);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'SalesPRO: Error al cerrar período');
    }
  };

  const reopen = async (id) => {
    try {
      await api.reopenClosure(id);
      toast.success('SalesPRO: Cierre reabierto');
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'SalesPRO: Error al reabrir');
    }
  };

  const rows = [
    ['Ingresos (ventas cobradas)', t.ingresos, 'text-green-600'],
    ['Compras', t.compras, 'text-red-600'],
    ['Gastos operativos', t.gastos, 'text-red-600'],
    ['Nómina', t.nomina, 'text-red-600'],
  ];

  return (
    <div className="space-y-4 mt-4">
      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        <div>
          <Label>Período</Label>
          <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="w-44" data-testid="period-input" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => downloadReport(api.exportPnlReport(companyId, period), `estado_resultados_${period}.xlsx`)} data-testid="export-pnl-button">
            <Download size={15} className="mr-1.5" /> Exportar Excel
          </Button>
          <Button variant="outline" size="sm" onClick={() => doClose('parcial')} data-testid="close-partial-button">
            <Lock size={15} className="mr-1.5" /> Cierre parcial
          </Button>
          <Button size="sm" onClick={() => doClose('definitivo')} data-testid="close-final-button">
            <Lock size={15} className="mr-1.5" /> Cierre definitivo
          </Button>
        </div>
      </div>

      <Card className="border border-[#E2E8F0] shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-lg font-bold">Estado de resultados — {period}</CardTitle>
          {closure && (
            <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full w-fit ${closure.status === 'definitivo' ? 'bg-red-50 text-red-700' : 'bg-orange-50 text-orange-700'}`} data-testid="closure-badge">
              {closure.status === 'definitivo' ? <Lock size={12} /> : <Unlock size={12} />}
              Período {closure.status} por {closure.closed_by_name || 'N/D'}
            </span>
          )}
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {rows.map(([label, val, cls]) => (
              <div key={label} className="flex justify-between items-center py-2 border-b border-[#F1F5F9]" data-testid={`pnl-row-${label}`}>
                <span className="text-sm text-[#475569]">{label}</span>
                <span className={`font-semibold ${cls}`}>{money(val)}</span>
              </div>
            ))}
            <div className="flex justify-between items-center pt-3">
              <span className="font-heading font-bold text-[#022C22]">Utilidad neta</span>
              <span className={`font-heading text-2xl font-black ${(t.utilidad || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`} data-testid="pnl-utilidad">{money(t.utilidad)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-[#E2E8F0] shadow-sm">
        <CardHeader><CardTitle className="font-heading text-base font-bold">Historial de cierres</CardTitle></CardHeader>
        <CardContent>
          {closures.length === 0 ? (
            <p className="text-sm text-[#94A3B8]">Aún no hay cierres registrados.</p>
          ) : (
            <div className="space-y-2">
              {closures.map(c => (
                <div key={c.closure_id} className="flex items-center justify-between p-3 bg-[#F8FAFC] rounded-md border border-[#E2E8F0]" data-testid={`closure-${c.period}`}>
                  <div>
                    <p className="font-medium text-sm">{c.period} · <span className={c.status === 'definitivo' ? 'text-red-600' : 'text-orange-600'}>{c.status}</span></p>
                    <p className="text-xs text-[#64748B]">Utilidad: {money(c.totals?.utilidad)} · {c.closed_by_name || ''}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => reopen(c.closure_id)} data-testid={`reopen-${c.period}`}>
                    <Unlock size={14} className="mr-1" /> Reabrir
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ---------------- Compras ---------------- */
function ComprasTab({ purchases, suppliers, products, banks, companyId, reload, bankName, supplierName }) {
  const [open, setOpen] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [payBank, setPayBank] = useState('');
  const [form, setForm] = useState({ supplier_id: '', items: [], tax_rate: 0, payment_status: 'pendiente', bank_account_id: '', notes: '' });

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { product_id: '', name: '', quantity: 1, cost: 0 }] }));
  const setItem = (idx, patch) => setForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const subtotal = form.items.reduce((a, it) => a + Number(it.cost || 0) * Number(it.quantity || 0), 0);
  const total = subtotal * (1 + Number(form.tax_rate || 0) / 100);

  const submit = async () => {
    if (form.items.length === 0) { toast.error('SalesPRO: Agrega al menos un ítem'); return; }
    if (form.payment_status === 'pagado' && !form.bank_account_id) { toast.error('SalesPRO: Selecciona el banco de pago'); return; }
    try {
      await api.createPurchase({ company_id: companyId, ...form, tax_rate: Number(form.tax_rate) });
      toast.success('SalesPRO: Compra registrada y stock cargado');
      setOpen(false);
      setForm({ supplier_id: '', items: [], tax_rate: 0, payment_status: 'pendiente', bank_account_id: '', notes: '' });
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'SalesPRO: Error al registrar compra');
    }
  };

  const payNow = async () => {
    if (!payBank) { toast.error('SalesPRO: Selecciona el banco'); return; }
    try {
      await api.payPurchase(payTarget.purchase_id, payBank);
      toast.success('SalesPRO: Compra pagada');
      setPayTarget(null);
      setPayBank('');
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'SalesPRO: Error al pagar');
    }
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={() => downloadReport(api.exportPurchasesReport(companyId), 'reporte_compras.xlsx')} data-testid="export-purchases-button">
          <Download size={16} className="mr-1.5" /> Exportar Excel
        </Button>
        <Button size="sm" onClick={() => { setForm(f => ({ ...f, items: [] })); setOpen(true); }} data-testid="new-purchase-button">
          <Plus size={16} className="mr-1.5" /> Nueva compra
        </Button>
      </div>

      {purchases.length === 0 ? (
        <p className="text-sm text-[#94A3B8] text-center py-8">Aún no hay compras registradas.</p>
      ) : (
        <div className="space-y-2">
          {purchases.map(p => (
            <Card key={p.purchase_id} className="border border-[#E2E8F0]" data-testid={`purchase-${p.purchase_id}`}>
              <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <p className="font-medium">{supplierName(p.supplier_id)} · {money(p.total)}</p>
                  <p className="text-xs text-[#64748B]">{(p.items || []).length} ítem(s) · {p.created_at?.slice(0, 10)} · {p.payment_status === 'pagado' ? `Pagado (${bankName(p.bank_account_id)})` : 'Pendiente por pagar'}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${p.payment_status === 'pagado' ? 'bg-green-50 text-green-700' : 'bg-orange-50 text-orange-700'}`}>{p.payment_status}</span>
                  {p.payment_status !== 'pagado' && (
                    <Button size="sm" variant="outline" onClick={() => { setPayTarget(p); setPayBank(banks[0]?.account_id || ''); }} data-testid={`pay-purchase-${p.purchase_id}`}>
                      <DollarSign size={14} className="mr-1" /> Pagar
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!payTarget} onOpenChange={(o) => { if (!o) { setPayTarget(null); setPayBank(''); } }}>
        <DialogContent className="max-w-md" data-testid="pay-purchase-dialog">
          <DialogHeader><DialogTitle>Pagar compra</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#64748B]">Total a pagar: <b className="text-[#022C22]">{money(payTarget?.total)}</b></p>
            <div>
              <Label>Banco de pago</Label>
              <select value={payBank} onChange={(e) => setPayBank(e.target.value)} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white h-10" data-testid="pay-bank-select">
                <option value="">— Selecciona —</option>
                {banks.map(b => <option key={b.account_id} value={b.account_id}>{b.name} ({money(b.balance)})</option>)}
              </select>
            </div>
            <Button onClick={payNow} className="w-full" data-testid="confirm-pay-purchase">Confirmar pago</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="purchase-dialog">
          <DialogHeader><DialogTitle>Nueva compra</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Proveedor</Label>
              <select value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white h-10" data-testid="purchase-supplier">
                <option value="">— Selecciona —</option>
                {suppliers.map(s => <option key={s.supplier_id} value={s.supplier_id}>{s.name}</option>)}
              </select>
            </div>

            <div className="flex items-center justify-between">
              <Label>Ítems</Label>
              <Button type="button" size="sm" variant="outline" onClick={addItem} data-testid="add-purchase-item"><Plus size={14} className="mr-1" />Agregar ítem</Button>
            </div>
            {form.items.map((it, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`purchase-item-${idx}`}>
                <select className="col-span-5 px-2 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white h-9" value={it.product_id}
                  onChange={(e) => { const prod = products.find(pr => pr.product_id === e.target.value); setItem(idx, { product_id: e.target.value, name: prod?.name || '', cost: prod?.cost || it.cost }); }}>
                  <option value="">Producto...</option>
                  {products.map(pr => <option key={pr.product_id} value={pr.product_id}>{pr.name}</option>)}
                </select>
                <Input className="col-span-3 h-9" type="number" min="1" placeholder="Cant." value={it.quantity} onChange={(e) => setItem(idx, { quantity: e.target.value })} />
                <Input className="col-span-3 h-9" type="number" step="0.01" placeholder="Costo" value={it.cost} onChange={(e) => setItem(idx, { cost: e.target.value })} />
                <Button type="button" size="icon" variant="ghost" className="col-span-1" onClick={() => removeItem(idx)}><Trash2 size={14} className="text-red-600" /></Button>
              </div>
            ))}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>IVA %</Label>
                <Input type="number" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} data-testid="purchase-tax" />
              </div>
              <div>
                <Label>Estado de pago</Label>
                <select value={form.payment_status} onChange={(e) => setForm({ ...form, payment_status: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white h-10" data-testid="purchase-payment-status">
                  <option value="pendiente">Pendiente por pagar</option>
                  <option value="pagado">Pagado</option>
                </select>
              </div>
            </div>
            {form.payment_status === 'pagado' && (
              <div>
                <Label>Banco de pago</Label>
                <select value={form.bank_account_id} onChange={(e) => setForm({ ...form, bank_account_id: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white h-10" data-testid="purchase-bank">
                  <option value="">— Selecciona —</option>
                  {banks.map(b => <option key={b.account_id} value={b.account_id}>{b.name} ({money(b.balance)})</option>)}
                </select>
              </div>
            )}
            <div className="flex justify-between items-center bg-[#F8FAFC] rounded-md p-3">
              <span className="text-sm text-[#64748B]">Total compra</span>
              <span className="font-heading text-xl font-bold" data-testid="purchase-total">{money(total)}</span>
            </div>
            <Button onClick={submit} className="w-full" data-testid="submit-purchase">Registrar compra</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Gastos ---------------- */
function GastosTab({ expenses, expCats, banks, companyId, reload, bankName }) {
  const [open, setOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [catName, setCatName] = useState('');
  const [form, setForm] = useState({ category_id: '', description: '', amount: '', bank_account_id: '' });

  const submit = async () => {
    if (!form.description.trim() || !form.amount) { toast.error('SalesPRO: Completa descripción y monto'); return; }
    try {
      await api.createExpense({ company_id: companyId, ...form, amount: Number(form.amount) });
      toast.success('SalesPRO: Gasto registrado');
      setOpen(false);
      setForm({ category_id: '', description: '', amount: '', bank_account_id: '' });
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'SalesPRO: Error al registrar gasto');
    }
  };

  const addCat = async () => {
    if (!catName.trim()) return;
    try {
      await api.createExpenseCategory({ company_id: companyId, name: catName.trim() });
      setCatName('');
      toast.success('SalesPRO: Categoría creada');
      reload();
    } catch (err) {
      toast.error('SalesPRO: Error al crear categoría');
    }
  };

  const del = async (id) => {
    if (!window.confirm('SalesPRO: ¿Eliminar gasto? Solo Master puede.')) return;
    try { await api.deleteExpense(id); toast.success('SalesPRO: Gasto eliminado'); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || 'SalesPRO: Error'); }
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={() => downloadReport(api.exportExpensesReport(companyId), 'reporte_gastos.xlsx')} data-testid="export-expenses-button">
          <Download size={16} className="mr-1.5" /> Exportar Excel
        </Button>
        <Button size="sm" variant="outline" onClick={() => setCatOpen(true)} data-testid="manage-expense-cats">Categorías</Button>
        <Button size="sm" onClick={() => setOpen(true)} data-testid="new-expense-button"><Plus size={16} className="mr-1.5" />Nuevo gasto</Button>
      </div>
      {expenses.length === 0 ? (
        <p className="text-sm text-[#94A3B8] text-center py-8">Aún no hay gastos registrados.</p>
      ) : (
        <div className="space-y-2">
          {expenses.map(e => (
            <Card key={e.expense_id} className="border border-[#E2E8F0]" data-testid={`expense-${e.expense_id}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium">{e.description} · {money(e.amount)}</p>
                  <p className="text-xs text-[#64748B]">{e.category_name || 'Sin categoría'} · {e.created_at?.slice(0, 10)} · {bankName(e.bank_account_id)}</p>
                </div>
                <Button variant="ghost" size="icon" onClick={() => del(e.expense_id)} data-testid={`delete-expense-${e.expense_id}`}><Trash2 size={14} className="text-red-600" /></Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="expense-dialog">
          <DialogHeader><DialogTitle>Nuevo gasto</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Categoría</Label>
              <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white h-10" data-testid="expense-category">
                <option value="">— Sin categoría —</option>
                {expCats.map(c => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
              </select>
            </div>
            <div><Label>Descripción</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="expense-description" /></div>
            <div><Label>Monto</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount" /></div>
            <div>
              <Label>Banco (opcional)</Label>
              <select value={form.bank_account_id} onChange={(e) => setForm({ ...form, bank_account_id: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white h-10" data-testid="expense-bank">
                <option value="">— Sin afectar banco —</option>
                {banks.map(b => <option key={b.account_id} value={b.account_id}>{b.name} ({money(b.balance)})</option>)}
              </select>
            </div>
            <Button onClick={submit} className="w-full" data-testid="submit-expense">Registrar gasto</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={catOpen} onOpenChange={setCatOpen}>
        <DialogContent className="max-w-md" data-testid="expense-cats-dialog">
          <DialogHeader><DialogTitle>Categorías de gasto</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input value={catName} onChange={(e) => setCatName(e.target.value)} placeholder="Ej: Alquiler, Servicios..." data-testid="expense-cat-input" />
              <Button onClick={addCat} data-testid="add-expense-cat">Crear</Button>
            </div>
            <div className="space-y-1">
              {expCats.map(c => (
                <div key={c.category_id} className="flex items-center justify-between px-3 py-2 bg-[#F8FAFC] rounded-md border border-[#E2E8F0]">
                  <span className="text-sm">{c.name}</span>
                  <Button variant="ghost" size="icon" onClick={async () => { await api.deleteExpenseCategory(c.category_id); reload(); }}><Trash2 size={14} className="text-red-600" /></Button>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Bancos ---------------- */
function BancosTab({ banks, companyId, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', bank_name: '', account_number: '', currency: 'USD', opening_balance: 0 });

  const submit = async () => {
    if (!form.name.trim()) { toast.error('SalesPRO: Ingresa un nombre'); return; }
    try {
      await api.createBank({ company_id: companyId, ...form, opening_balance: Number(form.opening_balance) });
      toast.success('SalesPRO: Cuenta creada');
      setOpen(false);
      setForm({ name: '', bank_name: '', account_number: '', currency: 'USD', opening_balance: 0 });
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'SalesPRO: Error al crear cuenta');
    }
  };

  const del = async (id) => {
    if (!window.confirm('SalesPRO: ¿Eliminar cuenta bancaria?')) return;
    try { await api.deleteBank(id); toast.success('SalesPRO: Cuenta eliminada'); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || 'SalesPRO: Error'); }
  };

  const totalBalance = banks.reduce((a, b) => a + Number(b.balance || 0), 0);

  return (
    <div className="space-y-4 mt-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#64748B]">Saldo total: <b className="text-[#022C22]">{money(totalBalance)}</b></p>
        <Button size="sm" onClick={() => setOpen(true)} data-testid="new-bank-button"><Plus size={16} className="mr-1.5" />Nueva cuenta</Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {banks.map(b => (
          <Card key={b.account_id} className="border border-[#E2E8F0]" data-testid={`bank-${b.account_id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="p-2 bg-[#EEF2FF] rounded-md"><Landmark size={20} className="text-[#4338CA]" /></div>
                <Button variant="ghost" size="icon" onClick={() => del(b.account_id)}><Trash2 size={14} className="text-red-600" /></Button>
              </div>
              <p className="font-heading font-bold mt-2">{b.name}</p>
              <p className="text-xs text-[#64748B]">{b.bank_name || ''} {b.account_number ? `· ${b.account_number}` : ''}</p>
              <p className="text-2xl font-heading font-black text-[#022C22] mt-2" data-testid={`bank-balance-${b.account_id}`}>{money(b.balance)}</p>
              <p className="text-xs text-[#94A3B8]">{b.currency}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="bank-dialog">
          <DialogHeader><DialogTitle>Nueva cuenta bancaria</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nombre de la cuenta</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Caja, Cuenta corriente..." data-testid="bank-name" /></div>
            <div><Label>Banco</Label><Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} data-testid="bank-bankname" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nº de cuenta</Label><Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} /></div>
              <div><Label>Moneda</Label><Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} /></div>
            </div>
            <div><Label>Saldo inicial</Label><Input type="number" step="0.01" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} data-testid="bank-opening" /></div>
            <Button onClick={submit} className="w-full" data-testid="submit-bank">Crear cuenta</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Proveedores ---------------- */
function ProveedoresTab({ suppliers, companyId, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', rif: '', phone: '', email: '', address: '' });

  const submit = async () => {
    if (!form.name.trim()) { toast.error('SalesPRO: Ingresa un nombre'); return; }
    try {
      await api.createSupplier({ company_id: companyId, ...form });
      toast.success('SalesPRO: Proveedor creado');
      setOpen(false);
      setForm({ name: '', rif: '', phone: '', email: '', address: '' });
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'SalesPRO: Error al crear proveedor');
    }
  };

  const del = async (id) => {
    if (!window.confirm('SalesPRO: ¿Eliminar proveedor?')) return;
    try { await api.deleteSupplier(id); toast.success('SalesPRO: Proveedor eliminado'); reload(); }
    catch (err) { toast.error('SalesPRO: Error'); }
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setOpen(true)} data-testid="new-supplier-button"><Plus size={16} className="mr-1.5" />Nuevo proveedor</Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {suppliers.map(s => (
          <Card key={s.supplier_id} className="border border-[#E2E8F0]" data-testid={`supplier-${s.supplier_id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="p-2 bg-[#F1F5F9] rounded-md"><Users2 size={20} className="text-[#64748B]" /></div>
                <Button variant="ghost" size="icon" onClick={() => del(s.supplier_id)}><Trash2 size={14} className="text-red-600" /></Button>
              </div>
              <p className="font-heading font-bold mt-2">{s.name}</p>
              {s.rif && <p className="text-xs text-[#64748B]">RIF: {s.rif}</p>}
              {s.phone && <p className="text-xs text-[#64748B]">{s.phone}</p>}
              {s.email && <p className="text-xs text-[#64748B]">{s.email}</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="supplier-dialog">
          <DialogHeader><DialogTitle>Nuevo proveedor</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="supplier-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>RIF</Label><Input value={form.rif} onChange={(e) => setForm({ ...form, rif: e.target.value })} /></div>
              <div><Label>Teléfono</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            </div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Dirección</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <Button onClick={submit} className="w-full" data-testid="submit-supplier">Crear proveedor</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
